import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { useApiData } from 'lib/api';
import { GroupUserAttributes } from 'components/groups';
import { AccessAndVisibilityCriteriaGroups } from '.';

import type { GetProductVisibilityCriteriaRes } from 'shared-types/responses';

type FormState = {
  conditions: { key: string; value: string | number | boolean }[];
  groupId: number;
};

interface Props {
  isAvailable: { groups: boolean; userAttributes: boolean };
  isEnabled: { groups: boolean; userAttributes: boolean };
  productId: number;
  type: 'achievement' | 'calendar-event' | 'competency' | 'competency-profile';
}

const AccessAndVisibilityCriteria = ({
  isAvailable,
  isEnabled,
  productId,
  type
}: Props) => {
  const baseUrl = `/admin/product-visibility/${type}/${productId}`;

  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const { data, loading, updateData } =
    useApiData<GetProductVisibilityCriteriaRes>({
      url: baseUrl,
      onlyIf: Boolean(type) && Boolean(productId)
    });

  const handleAddGroup = async (values: FormState) => {
    await api.post(baseUrl, {
      groupId: values.groupId
    });
    await updateData();
    dispatch(
      actions.addNotice({
        text: messages.commonAddGroupSuccess,
        type: 'success'
      })
    );
  };

  const handleRemoveGroup = async (criteriaId: number) => {
    await api.delete(`${baseUrl}/criteria/${criteriaId}`);
    await updateData();
    return true;
  };

  const handleAddOrEditUserAttribute = async (
    newConditions: any,
    conditionsIndex?: number
  ) => {
    await api.post(baseUrl, {
      userAttributes:
        conditionsIndex !== undefined
          ? [
              ...data?.criteria.userAttributes.slice(0, conditionsIndex),
              newConditions,
              ...data?.criteria.userAttributes.slice(conditionsIndex + 1)
            ]
          : [...data?.criteria.userAttributes, newConditions]
    });
    await updateData();
  };

  const handleRemoveUserAttribute = async (attributeIndex: number) => {
    await api.post(baseUrl, {
      userAttributes: data?.criteria.userAttributes.filter(
        (attr, i) => i !== attributeIndex
      )
    });
    await updateData();
    return true;
  };

  const disableGroups = !isEnabled?.groups
    ? messages.accessAndVisibilityVisibilityCriteriaGroupsDisabled
    : null;

  const disableUserAttributes = !isEnabled?.userAttributes
    ? messages.accessAndVisibilityVisibilityCriteriaUserAttributesDisabled
    : null;

  return !isAvailable ? null : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
      {isAvailable.groups ? (
        <AccessAndVisibilityCriteriaGroups
          disabledOverlayContent={disableGroups}
          groups={data?.criteria.groups}
          loading={loading}
          onAddGroup={handleAddGroup}
          onRemoveGroup={handleRemoveGroup}
        />
      ) : null}

      {isAvailable.userAttributes ? (
        <GroupUserAttributes
          disabledOverlayContent={disableUserAttributes}
          onAddUserAttribute={handleAddOrEditUserAttribute}
          onEditUserAttribute={handleAddOrEditUserAttribute}
          onRemoveUserAttribute={handleRemoveUserAttribute}
          userAttributes={data?.criteria.userAttributes}
        />
      ) : null}
    </div>
  );
};

export default AccessAndVisibilityCriteria;
